import type Phaser from 'phaser';

/**
 * Helper para desenho pixel-art em Phaser Graphics com coordenadas inteiras.
 * Usar sempre via integer pixels em coords lógicos; aplicar container.scale para enlarge.
 */
export class PixelCanvas {
  readonly g: Phaser.GameObjects.Graphics;

  constructor(g: Phaser.GameObjects.Graphics) {
    this.g = g;
  }

  px(x: number, y: number, color: number, alpha = 1): void {
    this.g.fillStyle(color, alpha);
    this.g.fillRect(Math.round(x), Math.round(y), 1, 1);
  }

  rect(x: number, y: number, w: number, h: number, color: number, alpha = 1): void {
    this.g.fillStyle(color, alpha);
    this.g.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
  }

  hLine(x: number, y: number, len: number, color: number, alpha = 1): void {
    if (len <= 0) return;
    this.g.fillStyle(color, alpha);
    this.g.fillRect(Math.round(x), Math.round(y), Math.round(len), 1);
  }

  vLine(x: number, y: number, len: number, color: number, alpha = 1): void {
    if (len <= 0) return;
    this.g.fillStyle(color, alpha);
    this.g.fillRect(Math.round(x), Math.round(y), 1, Math.round(len));
  }

  /** Elipse preenchida em pixels inteiros (algoritmo simples scanline). */
  ellipseFill(cx: number, cy: number, rx: number, ry: number, color: number): void {
    for (let y = -ry; y <= ry; y++) {
      const w = Math.round(rx * Math.sqrt(Math.max(0, 1 - (y * y) / (ry * ry))));
      if (w > 0) this.hLine(cx - w, cy + y, w * 2 + 1, color);
    }
  }

  /** Outline 1px de uma elipse (anel externo). */
  ellipseStroke(cx: number, cy: number, rx: number, ry: number, color: number): void {
    let lastW = -1;
    for (let y = -ry; y <= ry; y++) {
      const w = Math.round(rx * Math.sqrt(Math.max(0, 1 - (y * y) / (ry * ry))));
      if (w <= 0) continue;
      if (lastW < 0) {
        // primeira/última linha cheia (cap)
        this.hLine(cx - w, cy + y, w * 2 + 1, color);
      } else {
        this.px(cx - w, cy + y, color);
        this.px(cx + w, cy + y, color);
        // gap horizontal entre lastW e w
        if (w > lastW) {
          for (let x = lastW + 1; x < w; x++) {
            this.px(cx - x, cy + y, color);
            this.px(cx + x, cy + y, color);
          }
        }
      }
      lastW = w;
      if (y === ry || (y < ry && Math.round(rx * Math.sqrt(Math.max(0, 1 - ((y + 1) * (y + 1)) / (ry * ry)))) === 0)) {
        this.hLine(cx - w, cy + y, w * 2 + 1, color);
      }
    }
  }

  /** Círculo preenchido. */
  circleFill(cx: number, cy: number, r: number, color: number): void {
    this.ellipseFill(cx, cy, r, r, color);
  }

  clear(): void {
    this.g.clear();
  }
}
